import type { TypographyVariantsOptions } from '@mui/material/styles';

/**
 * Typography stack:
 *   - Plus Jakarta Sans (display, h1-h6, button) — voix vibrante caribéenne
 *   - Inter (body, captions) — texte courant lisible
 *
 * Self-host via `@fontsource/plus-jakarta-sans` + `@fontsource/inter` in the
 * consuming app (do not bundle here — keeps the theme package framework-agnostic).
 * See README.md for setup instructions.
 */

const FONT_DISPLAY = '"Plus Jakarta Sans", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const FONT_BODY = '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const typography: TypographyVariantsOptions = {
  fontFamily: FONT_BODY,
  // Variable font axis defaults — overridable per variant
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,

  // Display + headers use Plus Jakarta Sans
  h1: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 700,
    fontSize: 'clamp(2.5rem, 5vw, 4rem)',
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 700,
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    lineHeight: 1.15,
    letterSpacing: '-0.015em',
  },
  h3: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: 'clamp(1.25rem, 2.5vw, 1.75rem)',
    lineHeight: 1.25,
  },
  h5: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: '1.25rem',
    lineHeight: 1.3,
  },
  h6: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: '1.125rem',
    lineHeight: 1.4,
  },

  // Body uses Inter
  subtitle1: {
    fontFamily: FONT_BODY,
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.5,
  },
  subtitle2: {
    fontFamily: FONT_BODY,
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body1: {
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.6,
  },
  body2: {
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  caption: {
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.4,
    letterSpacing: '0.01em',
  },
  overline: {
    fontFamily: FONT_BODY,
    fontWeight: 600,
    fontSize: '0.75rem',
    lineHeight: 1.4,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },

  // Buttons use display font for caribéen punch
  button: {
    fontFamily: FONT_DISPLAY,
    fontWeight: 600,
    fontSize: '0.9375rem',
    lineHeight: 1.4,
    letterSpacing: '0.01em',
    textTransform: 'none',
  },
};

export const fontFamilies = {
  display: FONT_DISPLAY,
  body: FONT_BODY,
} as const;
